import axios from 'axios'
import { onUnmounted, ref } from 'vue'
import { useCurrentShop, useUser } from '#storefront/composables'
import { useState } from '#app'
import { useRoute } from '#app/composables/router'
import type { NotifySubscriptionResponse } from '~/types/notify'

export const useSnipesApi = () => {
  const isLoading = ref(false)
  const currentShop = useCurrentShop()
  const apiBasePath = `/api/snipes`

  const errorMessage = useState<string | null>('newsletter-signup', () => null)

  const clearErrorMessage = (): void => {
    errorMessage.value = null
  }

  const getRegistrationLanguage = (code: string): string => {
    return code.split('-')[0]
  }

  const fetch = async <T>(endpoint: string, payload: Record<string, any>) => {
    const apiPath = `${apiBasePath}/${endpoint.replace(/^\//, '')}`
    const headers = {
      'x-shop-id': currentShop.value?.shopId,
    }

    return await axios.post<T>(apiPath, payload, {
      headers,
    })
  }

  const fetchGet = async <T>(endpoint: string, email?: string) => {
    const apiPath = `${apiBasePath}/${endpoint.replace(/^\//, '')}`
    const { user } = await useUser()

    const headers = {
      'x-shop-id': currentShop.value?.shopId,
      'x-ref-key': email ?? user.value?.email,
    }

    return await axios.get<T>(apiPath, {
      headers,
    })
  }

  const notifyBackInStock = async (payload: {
    email: string
    source?: string
    product_sku: string
    additional_fields?: string[]
  }): Promise<NotifySubscriptionResponse> => {
    isLoading.value = true
    try {
      const response = await fetch<NotifySubscriptionResponse>(
        'notify/subscribe',
        {
          email: payload.email.toLowerCase(),
          product_sku: payload.product_sku,
          source: payload.source,
          additional_fields: payload.additional_fields,
        },
      )
      return response.data
    } catch (error) {
      console.error('Composable error subscribing to newsletter:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const notifyBackInStockConfirmation = async () => {
    const route = useRoute()
    const referenceKey = route.query.token
    try {
      const response = await fetch<NotifyStatusPayload>('notify/doi', {
        referenceKey,
      })
      return response.data
    } catch (error) {
      console.error('Composable error unsubscribing from newsletter:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const newsletterSubscribe = async (payload: {
    email: string
  }): Promise<NewsletterSubscriptionResponse> => {
    isLoading.value = true
    try {
      const response = await fetch<NewsletterSubscriptionResponse>(
        'newsletter/subscribe',
        {
          emailAddress: payload.email.toLowerCase(),
          registration_language: getRegistrationLanguage(
            currentShop.value.locale,
          ),
        },
      )
      return response.data
    } catch (error) {
      console.error('Composable error subscribing to newsletter:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const newsletterUnsubscribe = async () => {
    isLoading.value = true
    const { user } = await useUser()
    const route = useRoute()
    try {
      const response = await fetch<NewsletterUnsubscriptionPayload>(
        'newsletter/unsubscribe',
        {
          referenceKey:
            user.value?.referenceKey ?? route.query.token ?? user.value?.email,
        },
      )
      return response.data
    } catch (error) {
      console.error('Composable error unsubscribing from newsletter:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const newsletterConfirmation = async () => {
    const route = useRoute()
    const referenceKey = route.query.token
    try {
      const response = await fetch<NewsletterUnsubscriptionPayload>(
        'newsletter/doi',
        {
          referenceKey,
        },
      )
      return response.data
    } catch (error) {
      console.error('Composable error unsubscribing from newsletter:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const checkNewsletterSubscription = async (
    email?: string,
  ): Promise<NewsletterStatusResponse | undefined> => {
    isLoading.value = true
    try {
      const { data, status } = await fetchGet<NewsletterStatusResponse>(
        'newsletter/status',
        email,
      )
      return { ...data, status }
    } catch (error) {
      console.error('Composable error checking newsletter status:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  onUnmounted(() => clearErrorMessage())

  return {
    newsletterSubscribe,
    notifyBackInStock,
    newsletterUnsubscribe,
    notifyBackInStockConfirmation,
    checkNewsletterSubscription,
    newsletterConfirmation,
    isLoading,
    errorMessage,
  }
}
